import { getDisabledSubFeatures, getFeaturePermission, RouterLinks } from '@zeta-commons';
import {
  FullPageSpinner,
  getHighestRankedSubscriptionPlan,
  hasActiveSubscriptionPlans,
  useSubscriptionDetailsStore,
  useSubscriptionPlansStore,
  useUserAccessStore,
} from '@zeta-core';
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { CATEGORY_TO_ROUTES_MAP, ROUTE_TO_FEATURE_MAP } from '../constants';
import { useFlexiblePlanAccess } from '../hooks/use-flexible-plan-access';
import { isBusinessReviewPath } from './business-review-route';

const routeMatches = (pathname: string, allowedPath: string) => {
  if (pathname === allowedPath) return true;
  if (allowedPath === RouterLinks.clientInfoBusinessReviews && isBusinessReviewPath(pathname, allowedPath)) return true;
  if (allowedPath === RouterLinks.advisorCopilot && pathname.startsWith(`${RouterLinks.advisorCopilot}/`)) return true;
  return false;
};

function AccessGuard({ children }: { children: ReactNode }) {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const userAccessLoading = useUserAccessStore((state) => state.loading);
  const currentSubscription = useSubscriptionDetailsStore((state) => state.subscription);
  const subscriptionPlans = useSubscriptionPlansStore((state) => state.subscriptionPlans);
  const loading = useUserAccessStore((state) => state.loading);
  const subscriptionloading = useSubscriptionDetailsStore((state) => state.loading);
  const { pathname } = useLocation();
  const { hasFlexiblePlanAccess, isFlexiblePlanAccessLoading } = useFlexiblePlanAccess();
  const currentSubscriptionPlan = getHighestRankedSubscriptionPlan(currentSubscription, subscriptionPlans);
  const planType = currentSubscriptionPlan?.planType;
  const isFlexiblePlan = planType === 'FLEXIBLE_PLAN';
  const hasPaidSubscription = hasActiveSubscriptionPlans(currentSubscription) && currentSubscription?.planCategory === 'Paid';
  const planSubscriptionRoutes = [
    RouterLinks.advisorCopilot,
    RouterLinks.clientInfoPlans,
    RouterLinks.clientInfoReports,
    RouterLinks.clientInfoFinancialPlanningWorkspace,
    RouterLinks.clientInfoWorkplacePlansNew,
    RouterLinks.clientInfoWorkplacePlansSetup,
    RouterLinks.clientInfoWorkplaceReview,
  ];
  const disabledAdvisorOrgRoutesMap: Record<string, string[]> = {
    usermanagement: [RouterLinks.userManagement],
    billingmanagement: [RouterLinks.billingAndInvoice, '/profile-setup/invoice-detail', RouterLinks.feesAndChargesManagement],
    products: [RouterLinks.crm],
    clientcommunication: [RouterLinks.clientCommunication],
  };

  // If still loading, show spinner
  if (loading || subscriptionloading || userAccessLoading || isFlexiblePlanAccessLoading) {
    return (
      <>
        <FullPageSpinner />
      </>
    );
  }

  // If core data is missing after loading completes, show spinner
  // subscriptionPlans are static and should always be loaded
  if ((!currentSubscription && !hasFlexiblePlanAccess) || !userAccess || subscriptionPlans.length === 0) {
    return (
      <>
        <FullPageSpinner />
      </>
    );
  }

  // ===== STEP 1: Plan-level access check using CATEGORY_TO_ROUTES_MAP =====

  // Get enabled categories from current subscription plan features
  const enabledCategories = currentSubscriptionPlan?.activeCategories || [];
  const uniqueEnabledCategories = Array.from(new Set(enabledCategories));

  // Base allowed paths that are always accessible (no plan or feature check needed)
  const baseAllowedPaths: string[] = [
    RouterLinks.dashboard,
    RouterLinks.dashboardLink,
    RouterLinks.advisorCopilot,
    RouterLinks.changePassword,
    RouterLinks.myProfile,
    RouterLinks.clientInfoReports,
    RouterLinks.certificationsAndExpertise,
    RouterLinks.certificationsAndExpertiseCert,
    RouterLinks.certificationsAndExpertiseExpertise,
    RouterLinks.certificationsAndExpertiseProfessionalBackground,
    RouterLinks.meetingConfiguration,
    RouterLinks.documentManagement,
    RouterLinks.clientInfo,
    RouterLinks.clientInfoAskZeta,
    RouterLinks.clientInfoConflictResolution,
    RouterLinks.clientInfoBusinessProfile,
    RouterLinks.clientInfoBusinessReviews,
    RouterLinks.feesAndChargesManagement,
    RouterLinks.clientCommunication,
    RouterLinks.crm,
    RouterLinks.upgradePlan,
    RouterLinks.pricing,
    RouterLinks.profileSetup,
    RouterLinks.organisationProfile,
    RouterLinks.billingAndInvoice,
    RouterLinks.forbidden,
    RouterLinks.root,
    RouterLinks.support,
    RouterLinks.supportTicket,
    RouterLinks.training,
    RouterLinks.completePayment,
  ];
  // Collect all plan-level allowed paths based on enabled categories
  const categoryBasedPaths: string[] = [];
  uniqueEnabledCategories.forEach((category) => {
    const routes = CATEGORY_TO_ROUTES_MAP[category];
    if (routes && Array.isArray(routes)) {
      categoryBasedPaths.push(...routes);
    }
    categoryBasedPaths.push(...CATEGORY_TO_ROUTES_MAP['default']);
  });
  // Combine base paths with category-based paths for plan-level access
  const planLevelAllowedPaths = [...baseAllowedPaths, ...categoryBasedPaths];

  // Filter paths based on plan type
  const filteredPlanPaths = planLevelAllowedPaths.filter((path) => {
    if (planType === 'FREE_TRIAL_PLAN') {
      const pathsToRemove = [RouterLinks.userManagement];
      return !pathsToRemove.includes(path);
    }
    return true; // keep all for other plan types
  });

  // Remove duplicates from plan-level paths
  const uniquePlanLevelPaths = Array.from(new Set(filteredPlanPaths));
  const access = userAccess?.access;
  const hasConditionalReadAccess = (featureName: string) => (access?.[featureName] || []).some((permission) => permission?.Type === 'Read');
  const isFlexPlanReadOnlyAccess =
    getFeaturePermission(access, 'Advisor-Client-List-View').hasReadPermission &&
    !getFeaturePermission(access, 'Advisor-Client-List-View').hasWritePermission &&
    getFeaturePermission(access, 'AdvisorTaskWorkflow').hasReadPermission &&
    !getFeaturePermission(access, 'AdvisorTaskWorkflow').hasWritePermission &&
    getFeaturePermission(access, 'AdvisorMeeting').hasReadPermission &&
    !getFeaturePermission(access, 'AdvisorMeeting').hasWritePermission &&
    hasConditionalReadAccess('HouseholdModeling') &&
    !getFeaturePermission(access, 'HouseholdModeling').hasWritePermission &&
    hasConditionalReadAccess('SecureDocumentStorage') &&
    !getFeaturePermission(access, 'SecureDocumentStorage').hasWritePermission;
  const flexiblePlanAllowedPaths = hasFlexiblePlanAccess
    ? isFlexPlanReadOnlyAccess
      ? [
          ...(ROUTE_TO_FEATURE_MAP['Advisor-Client-List-View']?.routes || []),
          ...(ROUTE_TO_FEATURE_MAP.AdvisorTaskWorkflow?.routes || []),
          ...(ROUTE_TO_FEATURE_MAP.AdvisorMeeting?.routes || []),
          ...(ROUTE_TO_FEATURE_MAP.HouseholdModeling?.routes || []),
          ...(ROUTE_TO_FEATURE_MAP.SecureDocumentStorage?.routes || []),
          RouterLinks.clientInfo,
          RouterLinks.clientInfoConflictResolution,
          RouterLinks.support,
        ]
      : [
          RouterLinks.organisationProfile,
          RouterLinks.myProfile,
          RouterLinks.userManagement,
          RouterLinks.riskAssessment,
          RouterLinks.financialPlanning,
          RouterLinks.adviceTemplates,
          RouterLinks.documentManagement,
          RouterLinks.crm,
          RouterLinks.clientCommunication,
          RouterLinks.feesAndChargesManagement,
          RouterLinks.factFindConfiguration,
          RouterLinks.meetingConfiguration,
          RouterLinks.certificationsAndExpertise,
          RouterLinks.certificationsAndExpertiseCert,
          RouterLinks.certificationsAndExpertiseExpertise,
          RouterLinks.certificationsAndExpertiseProfessionalBackground,
        ]
    : [];
  const subscriptionPlanAllowedPaths = hasPaidSubscription ? planSubscriptionRoutes : [];
  const effectivePlanLevelPaths = Array.from(new Set([...uniquePlanLevelPaths, ...flexiblePlanAllowedPaths, ...subscriptionPlanAllowedPaths]));

  // Check if the current path is allowed at plan level
  const isPlanLevelAllowed = isFlexiblePlan ? true : effectivePlanLevelPaths.some((path) => routeMatches(pathname, path));

  // If not allowed at plan level, redirect to upgrade plan
  if (!isPlanLevelAllowed) {
    return <Navigate to={RouterLinks.upgradePlan} replace />;
  }

  // ===== STEP 2: Feature-level access check using ROUTE_TO_FEATURE_MAP =====
  // This ensures that even if a route is accessible at plan level (through categories),
  // the user still needs explicit feature access. If feature access is revoked, user gets 403.

  // Get the list of feature names from the store
  const availableFeatures = Object.keys(access || {});

  // Collect all feature-level allowed paths
  // IMPORTANT: We start with ONLY baseAllowedPaths, NOT categoryBasedPaths
  // This ensures routes require explicit feature access even if plan allows them
  const featureLevelAllowedPaths: string[] = [...baseAllowedPaths];

  // For each available feature, add its allowed routes
  availableFeatures.forEach((feature) => {
    const featureConfig = ROUTE_TO_FEATURE_MAP[feature as keyof typeof ROUTE_TO_FEATURE_MAP];

    if (featureConfig) {
      // Get the status conditions for this feature from the store
      const statusConditions = access?.[feature]?.map((item) => {
        if (!item.StatusCondition) {
          return 'default';
        }
        return item.StatusCondition;
      });
      // If the feature has routes directly
      if ('routes' in featureConfig && Array.isArray(featureConfig.routes)) {
        featureLevelAllowedPaths.push(...featureConfig.routes);
      }

      // Add routes for each status condition that matches
      statusConditions?.forEach((condition) => {
        if (condition && condition in featureConfig && Array.isArray(featureConfig[condition as keyof typeof featureConfig])) {
          featureLevelAllowedPaths.push(...(featureConfig[condition as keyof typeof featureConfig] as string[]));
        }
      });
    }
  });

  // Remove duplicates from feature-level paths
  const uniqueFeatureLevelPaths = Array.from(new Set(featureLevelAllowedPaths));

  // Check if the current path is allowed at feature level
  const isFeatureLevelAllowed = uniqueFeatureLevelPaths.some((path) => routeMatches(pathname, path));

  const disabledAdvisorOrgRoutes = getDisabledSubFeatures(access, 'AdvisorOrgConfig').flatMap((subFeature) => disabledAdvisorOrgRoutesMap[subFeature] || []);

  if (disabledAdvisorOrgRoutes.includes(pathname)) {
    return <Navigate to={RouterLinks.forbidden} replace />;
  }

  // CRITICAL: If plan allows the route but feature access is missing, show 403
  // This handles the case where a route is in the plan (categoryBasedPaths)
  // but the specific feature for that route has been revoked from the user
  if (!isFeatureLevelAllowed) {
    return <Navigate to={RouterLinks.forbidden} replace />;
  }

  // Both plan-level AND feature-level checks passed, allow access
  return <>{children}</>;
}

export default AccessGuard;
